import { useEffect } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Alert, Select, Tabs } from 'antd';
import { EditOutlined, ProjectOutlined, SearchOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import { ContentBackground } from '@/components/content';
import { HowToCard } from '@/components/how-to-card';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import { PageHeader } from '@/components/page-header';
import { ROLES_PERMISSIONS } from '@/constants';
import { QueryParamsProvider } from '@/contexts/query-params/QueryParamsProvider';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { API } from '@/services/api.service';
import LedgerCard from './_components/LedgerCard';
import SuppliersList from './_components/SuppliersList';
import { str } from './_components/format';

const DEFAULT_PARAMS = Object.freeze({ offset: '1', limit: '50', tab: 'list' });
const HOW_TO_ICONS = [SearchOutlined, UserOutlined, EditOutlined];

/**
 * Suppliers („Dobavljači") under Invoices: the company's suppliers as its
 * incoming invoices name them — for everyone who sees those invoices — and,
 * where the company has its ledger switched on, the book figures next to them
 * and a "By project" tab with the project card. A supplier opens its profile.
 * Company, tab, search and page live in the address.
 *
 * @returns {JSX.Element}
 */
function SuppliersContent() {
  const { t } = useTranslation(['suppliers', 'menus']);
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const company = str(queryParams.company);

  const { data: companies = [], isLoading: companiesLoading } = useQuery({
    queryKey: ['suppliers', 'companies'],
    queryFn: () => API.suppliers.getCompanies(),
  });
  useEffect(() => {
    if (!company && companies.length === 1) setQueryParams({ company: String(companies[0]._id), offset: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, company]);

  // The list answer says whether the ledger parts open for this company.
  const listQuery = {
    search: str(queryParams.search) || undefined,
    sort: str(queryParams.sort) || undefined,
    order: str(queryParams.order) || undefined,
    offset: str(queryParams.offset) || '1',
    limit: str(queryParams.limit) || '50',
  };
  const list = useQuery({
    queryKey: ['suppliers', company, 'list', listQuery],
    queryFn: () => API.suppliers.getSuppliers(company, listQuery),
    enabled: Boolean(company),
    retry: false,
  });
  const ledger = Boolean(list.data?.ledger);
  const tab = ledger && queryParams.tab === 'project' ? 'project' : 'list';
  const showCompanySelect = companies.length > 1;
  const howToSteps = t('howTo.steps', { returnObjects: true });

  return (
    <RootLayout metadata={{ title: t('suppliers', { ns: 'menus' }) }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />
      <Section>
        <ContainerFluid className="foreground-wrapper">
          {showCompanySelect && (
            <PageHeader>
              <div className="flex w-full flex-wrap items-center gap-2 bg-white p-1">
                <Select
                  showSearch
                  optionFilterProp="label"
                  style={{ width: 420 }}
                  popupMatchSelectWidth={false}
                  placeholder={t('company')}
                  value={company || undefined}
                  onChange={(v) => setQueryParams({ company: v, tab: 'list', search: undefined, offset: 1, orgUnit: undefined, partner: undefined, account: undefined })}
                  options={companies.map((c) => ({ value: String(c._id), label: c.name }))}
                />
              </div>
            </PageHeader>
          )}

          <div className={clsx('rounded-lg bg-white p-4', showCompanySelect && 'mt-3')}>
            <p className="mb-3 text-sm opacity-70">{t('subtitle')}</p>
            <HowToCard
              title={t('howTo.title')}
              sections={[
                {
                  title: t('howTo.section'),
                  steps: (Array.isArray(howToSteps) ? howToSteps : []).map((s, i) => ({ ...s, icon: HOW_TO_ICONS[i] })),
                },
              ]}
            />
            {!companiesLoading && !companies.length && <Alert type="info" showIcon message={t('noCompanies')} />}
            {companies.length > 0 && !company && <Alert type="info" showIcon message={t('pickCompany')} />}

            {company && (
              <Tabs
                activeKey={tab}
                onChange={(v) => setQueryParams({ tab: v, offset: 1, uOffset: 1 })}
                items={[
                  {
                    key: 'list',
                    label: t('tabs.list'),
                    icon: <ShopOutlined />,
                    children: <SuppliersList company={company} list={list} queryParams={queryParams} setQueryParams={setQueryParams} />,
                  },
                  ledger && {
                    key: 'project',
                    label: t('tabs.project'),
                    icon: <ProjectOutlined />,
                    children: (
                      <LedgerCard company={company} view="project" queryParams={queryParams} setQueryParams={setQueryParams} />
                    ),
                  },
                ].filter(Boolean)}
              />
            )}
          </div>
        </ContainerFluid>
      </Section>
    </RootLayout>
  );
}

function SuppliersWithParams() {
  return (
    <QueryParamsProvider defaultParams={DEFAULT_PARAMS}>
      <SuppliersContent />
    </QueryParamsProvider>
  );
}

const SuppliersPage = withPrivateRoute(SuppliersWithParams, ROLES_PERMISSIONS.SUPPLIERS.VIEW);
SuppliersPage.displayName = 'SuppliersPage';

export default SuppliersPage;
